/**
 * Configuration rule Test & Simulation — per-module scenario simulators.
 */

import { createModuleSimulator } from './createModuleSimulator';
import { labelFromOptions } from './shared';

import {
  VAL_TRIGGER_OPTIONS,
  getBaseCheckLabel,
  getTriggerLabel,
  getValidationModuleLabel,
} from '../validationConfig';

import {
  APPLICATION_ACCESS_OPTIONS,
  EVIDENCE_REQUIREMENT_OPTIONS,
  DECISION_PATH_OPTIONS,
  INFORMATION_REQUEST_RULE_OPTIONS,
  REAPPLICATION_RULE_OPTIONS,
  getComplianceTypeLabel,
  getComplianceNameLabel,
  normalizeComplianceForm,
} from '../complianceRulesConfig';

import {
  WF_MODULE_OPTIONS,
  WF_TRIGGER_TYPE_OPTIONS,
  formatWorkflowTriggerDisplay,
  getStatusesForRecordType,
  getStatusLabel,
} from '../workflowConfig';

import {
  CHECK_METHOD_OPTIONS,
  MATCH_ACTION_OPTIONS,
  getModuleLabel as getContentControlModuleLabel,
  getContentAreaLabel,
  getContentIssueLabel,
  formatTriggerList,
} from '../contentControlConfig';

import {
  DISPLAY_STATE_OPTIONS,
  SOURCE_RESULT_OPTIONS,
  VISIBILITY_AUDIENCE_OPTIONS,
  getDisplayAreaLabel,
  getDisplayStateLabel,
  getPublishingActionLabel,
  getPublishingModuleLabel,
  getSourceResultLabel,
  getSourceResultsForDisplayArea,
  getVisibilityAudienceLabel,
} from '../publishingConfig';

import {
  FILE_FORMAT_OPTIONS,
  MISSING_DOCUMENT_RESULT_OPTIONS,
  labelEntryPoint,
  getDocumentModuleLabel,
} from '../documentsConfig';

import {
  ACTION_TYPE_OPTIONS,
  formatRequirementPreview,
  getActionTargetLabel,
  getActionTypeLabel,
  getCaseActionModuleLabel,
  getRecordTypeLabel as getCaseActionRecordTypeLabel,
  isEvidenceSourceAvailable,
  normalizeCaseActionForm,
  requiresEvidenceSource,
} from '../caseActionsConfig';

import {
  formatLimitDisplay,
  getLimitForLabel,
  getLimitTypeLabel,
  getRequirementLabel,
  getUnitLabel,
  normalizeLimitForm,
  parseEnteredNumber,
  valueSatisfiesRule,
} from '../limitsThresholdsConfig';

import {
  getAutomatedJobLabel,
  getAutomationModuleLabel,
  getRecordTypeLabel,
} from '../automationConfig';

import {
  LIFECYCLE_MODULES,
  END_EVENT_OPTIONS,
  LIFECYCLE_TRIGGER_OPTIONS,
  getArchiveActionLabel,
  getEndEventLabel,
  normalizeLifecycleForm,
} from '../lifecycleConfig';

import {
  getFinalActionLabel,
  getRetentionPeriodLabel,
  getRetentionRecordTypeLabel,
  getStartsWhenLabel,
  labelRetentionPeriod,
  normalizeRetentionForm,
} from '../retentionConfig';

import {
  FLAGGING_SUBMITTED_BY_OPTIONS,
  SUBMISSION_RESULT_OPTIONS,
  getFlagTypeLabel,
  getReasonItemsForSet,
} from '../flaggingConfig';

import {
  formatTimingReferenceDetail,
  getEventDefinition,
  getEventDisplay,
  getMethodLabel,
  getRecipientLabel,
  getSendToLabel,
  getTimingLabel,
  getTypeLabel,
  needsRepeatInterval,
  normalizeNotificationForm,
} from '../notificationsConfig';

// ——— shared helpers ———

const SCENARIO_NAME_FIELD = {
  key: 'scenarioName',
  label: 'Scenario Name',
  type: 'text',
  required: true,
  default: 'Default scenario',
};

function compareExpected(form, evaluation, checks) {
  const actualValues = evaluation.actualValues || {};
  return checks.map(({ key, label, options }) => {
    const expected = form[key];
    const got = actualValues[key];
    const pass = expected === got;
    return {
      pass,
      reason: pass
        ? ''
        : `Expected ${label} (${labelFromOptions(options, expected)}) did not match the actual result (${labelFromOptions(options, got)}).`,
    };
  });
}

function mapFinalActionToOutcome(finalAction, periodElapsed, checksPass) {
  if (!periodElapsed) return 'retain';
  if (!checksPass) return 'retain';
  if (finalAction === 'delete_permanently' || finalAction === 'purge_record') return 'delete';
  if (finalAction === 'anonymise' || finalAction === 'de_identify_record' || finalAction === 'keep_metadata_only') return 'anonymize';
  return 'retain';
}

// ——— expected outcome option sets ———

const VALIDATION_RESULT_OPTIONS = [
  { value: 'pass', label: 'Passed' },
  { value: 'fail', label: 'Failed' },
  { value: 'unavailable', label: 'Unavailable' },
];

const COMPLIANCE_SUBMISSION_OPTIONS = [
  { value: 'new', label: 'New Submission' },
  { value: 'resubmission', label: 'Resubmission' },
];

const SCREENING_OUTCOME_OPTIONS = [
  { value: 'pass', label: 'Pass' },
  { value: 'flag', label: 'Flag' },
  { value: 'block', label: 'Block' },
];

const DOCUMENT_UPLOAD_RESULT_OPTIONS = [
  { value: 'allowed', label: 'Upload Allowed' },
  { value: 'blocked', label: 'Upload Blocked' },
];

const ACTION_AVAILABILITY_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'blocked', label: 'Blocked' },
];

const LIMIT_COMPARISON_OPTIONS = [
  { value: 'within', label: 'Within restriction' },
  { value: 'outside', label: 'Outside restriction' },
  { value: 'unavailable', label: 'Measurement unavailable' },
];

const LIFECYCLE_OUTCOME_OPTIONS = [
  { value: 'archived', label: 'Archived' },
  { value: 'restored', label: 'Restored' },
  { value: 'no_change', label: 'No Change' },
];

const RETENTION_OUTCOME_OPTIONS = [
  { value: 'retain', label: 'Retain' },
  { value: 'delete', label: 'Delete' },
  { value: 'anonymize', label: 'Anonymize' },
];

const FLAG_OUTCOME_OPTIONS = [
  { value: 'created', label: 'Flag Created' },
  { value: 'suppressed', label: 'Flag Suppressed' },
];

const NOTIFICATION_OUTCOME_OPTIONS = [
  { value: 'sent', label: 'Sent' },
  { value: 'do_not_send', label: 'Do not send' },
  { value: 'unavailable', label: 'Unavailable' },
];

const RECORD_STATE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

// ——— module simulators ———

const validationSimulator = createModuleSimulator({
  moduleKey: 'validation',
  summaryFields: [
    { key: 'module', label: 'Module', format: (r) => getValidationModuleLabel(r.module) },
    { key: 'entryPoint', label: 'Entry Point', format: (r) => r.entryPoint || '—' },
    { key: 'trigger', label: 'Trigger', format: (r) => getTriggerLabel(r.trigger || r.formAction) },
    { key: 'inputField', label: 'Input Field', format: (r) => r.inputField || '—' },
    { key: 'baseCheck', label: 'Base Check', format: (r) => getBaseCheckLabel(r.baseCheck || r.ruleType) },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    {
      key: 'trigger',
      label: 'Trigger',
      type: 'select',
      options: VAL_TRIGGER_OPTIONS,
      default: (rule) => rule?.trigger || rule?.formAction || 'submit',
    },
    {
      key: 'fieldValueProvided',
      label: 'Field Value Provided',
      type: 'toggle',
      default: true,
    },
    {
      key: 'valueMatchesRule',
      label: 'Value Matches Rule',
      type: 'toggle',
      default: true,
    },
    {
      key: 'checkAvailable',
      label: 'Required Check Available',
      type: 'toggle',
      default: true,
    },
  ],
  expectedFields: [
    {
      key: 'expectedValidationResult',
      label: 'Expected Validation Result',
      type: 'select',
      required: true,
      options: VALIDATION_RESULT_OPTIONS,
      default: 'pass',
    },
  ],
  evaluate(rule, form) {
    const ruleTrigger = rule?.trigger || rule?.formAction || 'submit';
    const actionMatch = (form.trigger || form.formAction) === ruleTrigger;
    const provided = Boolean(form.fieldValueProvided);
    const matches = Boolean(form.valueMatchesRule);
    const available = form.checkAvailable !== false;
    const requiredCondition = (rule?.conditions || []).find((condition) => condition.type === 'required_check');
    const isRequired = Boolean(requiredCondition);

    let validationResult = 'pass';
    let messageShown = 'Validation passed.';
    let nextAction = 'Continue';

    if (!actionMatch) {
      validationResult = 'pass';
      messageShown = 'Rule does not apply to this trigger.';
      nextAction = 'No validation applied';
    } else if (!available) {
      validationResult = 'unavailable';
      messageShown = 'We could not complete the required check. Please try again.';
      nextAction = 'Retry the required check';
    } else if (isRequired && !provided) {
      validationResult = 'fail';
      messageShown = requiredCondition.message || 'Required field is missing.';
      nextAction = 'Provide the required field value';
    } else if (!provided) {
      validationResult = 'pass';
      messageShown = 'Optional blank allowed. Value-dependent checks were skipped.';
      nextAction = 'Continue';
    } else if (!matches) {
      validationResult = 'fail';
      messageShown = rule.baseCheckMessage || rule.validationMessage || 'Validation failed.';
      nextAction = 'Correct the field value';
    }

    return {
      actual: { expectedValidationResult: validationResult },
      actualValues: { expectedValidationResult: validationResult },
      messageShown,
      nextAction,
      displayResults: [
        { label: 'Trigger', value: getTriggerLabel(form.trigger || form.formAction) },
        { label: 'Field Provided', value: provided ? 'Yes' : 'No' },
        { label: 'Value Matches', value: matches ? 'Yes' : 'No' },
        { label: 'Check Available', value: available ? 'Yes' : 'No' },
        { label: 'Validation Result', value: labelFromOptions(VALIDATION_RESULT_OPTIONS, validationResult) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedValidationResult', label: 'Validation Result', options: VALIDATION_RESULT_OPTIONS },
    ]);
  },
});

const complianceSimulator = createModuleSimulator({
  moduleKey: 'compliance',
  summaryFields: [
    { key: 'complianceType', label: 'Compliance Type', format: (r) => getComplianceTypeLabel(r.complianceType) },
    { key: 'complianceName', label: 'Compliance Name', format: (r) => getComplianceNameLabel(r.complianceType, r.complianceName) },
    { key: 'applicationAccess', label: 'Application Access', format: (r) => labelFromOptions(APPLICATION_ACCESS_OPTIONS, normalizeComplianceForm(r).applicationAccess) },
    { key: 'evidenceRequirement', label: 'Evidence Requirement', format: (r) => labelFromOptions(EVIDENCE_REQUIREMENT_OPTIONS, normalizeComplianceForm(r).evidenceRequirement) },
    { key: 'decisionPath', label: 'Decision Path', format: (r) => labelFromOptions(DECISION_PATH_OPTIONS, normalizeComplianceForm(r).decisionPath) },
    { key: 'informationRequestRule', label: 'Information Request', format: (r) => labelFromOptions(INFORMATION_REQUEST_RULE_OPTIONS, normalizeComplianceForm(r).informationRequestRule) },
    { key: 'reapplicationRule', label: 'Reapplication Rule', format: (r) => labelFromOptions(REAPPLICATION_RULE_OPTIONS, normalizeComplianceForm(r).reapplicationRule) },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    {
      key: 'submissionType',
      label: 'Submission Type',
      type: 'select',
      options: COMPLIANCE_SUBMISSION_OPTIONS,
      default: 'new',
    },
    {
      key: 'evidenceProvided',
      label: 'Evidence Provided',
      type: 'toggle',
      default: true,
    },
  ],
  expectedFields: [
    {
      key: 'expectedApplicationAccess',
      label: 'Expected Application Access',
      type: 'select',
      required: true,
      options: APPLICATION_ACCESS_OPTIONS,
      default: 'allowed',
    },
    {
      key: 'expectedDecisionPath',
      label: 'Expected Decision Path',
      type: 'select',
      required: true,
      options: DECISION_PATH_OPTIONS,
      default: (rule) => normalizeComplianceForm(rule).decisionPath || 'approve_or_reject',
    },
  ],
  evaluate(rule, form) {
    const normalized = normalizeComplianceForm(rule);
    let applicationAccess = normalized.applicationAccess || 'allowed';
    let decisionPath = normalized.decisionPath || 'approve_or_reject';
    let messageShown = 'Application may proceed.';
    let nextAction = 'Continue submission review';

    if (applicationAccess === 'blocked') {
      messageShown = 'Application access is blocked for this compliance item.';
      nextAction = 'Review application access rule';
    } else if (form.submissionType === 'resubmission' && normalized.reapplicationRule === 'blocked') {
      applicationAccess = 'blocked';
      messageShown = 'Resubmission is not allowed.';
      nextAction = 'Close or reject the application';
    } else if (normalized.evidenceRequirement === 'required' && !form.evidenceProvided) {
      applicationAccess = 'blocked';
      decisionPath = normalized.decisionPath;
      messageShown = 'Required evidence has not been provided.';
      nextAction = 'Collect required evidence';
    } else if (normalized.evidenceRequirement === 'optional' && !form.evidenceProvided) {
      messageShown = 'Optional evidence was not provided.';
      nextAction = 'Proceed without evidence or request later';
    }

    return {
      actual: { expectedApplicationAccess: applicationAccess, expectedDecisionPath: decisionPath },
      actualValues: { expectedApplicationAccess: applicationAccess, expectedDecisionPath: decisionPath },
      messageShown,
      nextAction,
      displayResults: [
        { label: 'Submission Type', value: labelFromOptions(COMPLIANCE_SUBMISSION_OPTIONS, form.submissionType) },
        { label: 'Evidence Provided', value: form.evidenceProvided ? 'Yes' : 'No' },
        { label: 'Application Access', value: labelFromOptions(APPLICATION_ACCESS_OPTIONS, applicationAccess) },
        { label: 'Decision Path', value: labelFromOptions(DECISION_PATH_OPTIONS, decisionPath) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedApplicationAccess', label: 'Application Access', options: APPLICATION_ACCESS_OPTIONS },
      { key: 'expectedDecisionPath', label: 'Decision Path', options: DECISION_PATH_OPTIONS },
    ]);
  },
});

const workflowSimulator = createModuleSimulator({
  moduleKey: 'workflow',
  summaryFields: [
    { key: 'module', label: 'Module', format: (r) => labelFromOptions(WF_MODULE_OPTIONS, r.module) },
    { key: 'recordType', label: 'Record Type', format: (r) => r.recordType || '—' },
    { key: 'currentStatus', label: 'Current Status', format: (r) => getStatusLabel(r.recordType, r.currentStatus) },
    { key: 'nextStatus', label: 'Next Status', format: (r) => getStatusLabel(r.recordType, r.nextStatus) },
    { key: 'triggerType', label: 'Trigger Type', format: (r) => labelFromOptions(WF_TRIGGER_TYPE_OPTIONS, r.triggerType) },
    { key: 'trigger', label: 'Trigger', format: (r) => formatWorkflowTriggerDisplay(r) },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    {
      key: 'currentStatus',
      label: 'Current Status',
      type: 'select',
      options: (rule) => getStatusesForRecordType(rule?.recordType),
      default: (rule) => rule?.currentStatus || getStatusesForRecordType(rule?.recordType)[0]?.value || '',
    },
    {
      key: 'triggerEvent',
      label: 'Trigger Event',
      type: 'select',
      options: (rule) => {
        const cond = String(
          rule?.triggerEvent || rule?.timeStartsFrom || rule?.triggerCondition || '',
        ).trim();
        return cond ? [{ value: cond, label: cond }] : [{ value: 'event', label: 'Configured Event' }];
      },
      default: (rule) => rule?.triggerEvent || rule?.timeStartsFrom || rule?.triggerCondition || 'event',
    },
  ],
  expectedFields: [
    {
      key: 'expectedNextStatus',
      label: 'Expected Next Status',
      type: 'select',
      required: true,
      options: (rule) => getStatusesForRecordType(rule?.recordType),
      default: (rule) => rule?.nextStatus || '',
    },
  ],
  evaluate(rule, form) {
    const configuredTrigger =
      rule?.triggerEvent || rule?.timeStartsFrom || rule?.triggerCondition || form.triggerEvent;
    const statusMatch = form.currentStatus === (rule?.currentStatus || form.currentStatus);
    const triggerMatch = form.triggerEvent === configuredTrigger;
    const applies = statusMatch && triggerMatch;
    const nextStatus = applies ? rule?.nextStatus || form.currentStatus : form.currentStatus;

    return {
      actual: { expectedNextStatus: nextStatus },
      actualValues: { expectedNextStatus: nextStatus },
      messageShown: applies ? 'Workflow transition applied.' : 'Workflow rule did not match.',
      nextAction: applies ? `Move record to ${getStatusLabel(rule.recordType, nextStatus)}` : 'Review status and trigger',
      displayResults: [
        { label: 'Current Status', value: getStatusLabel(rule.recordType, form.currentStatus) },
        { label: 'Trigger Event', value: form.triggerEvent || '—' },
        { label: 'Rule Matched', value: applies ? 'Yes' : 'No' },
        { label: 'Next Status', value: getStatusLabel(rule.recordType, nextStatus) },
      ],
    };
  },
  compareChecks(form, evaluation, rule) {
    return compareExpected(form, evaluation, [
      { key: 'expectedNextStatus', label: 'Next Status', options: getStatusesForRecordType(rule?.recordType) },
    ]);
  },
});

const contentControlSimulator = createModuleSimulator({
  moduleKey: 'content-control',
  summaryFields: [
    { key: 'module', label: 'Module', format: (r) => getContentControlModuleLabel(r.module) },
    { key: 'contentArea', label: 'Content Area', format: (r) => getContentAreaLabel(r.contentArea || r.surface) },
    { key: 'contentIssue', label: 'Content Issue', format: (r) => getContentIssueLabel(r.contentIssue || r.breachCategory) },
    {
      key: 'detectionMethods',
      label: 'Detection Methods',
      format: (r) => (r.detectionMethods || (r.checkMethod ? [r.checkMethod] : [])).map((method) => labelFromOptions(CHECK_METHOD_OPTIONS, method)).join(' · ') || '—',
    },
    {
      key: 'confirmedAction',
      label: 'Confirmed Match Action',
      format: (r) => labelFromOptions(MATCH_ACTION_OPTIONS, r.confirmedAction || r.primaryAction),
    },
    {
      key: 'triggers',
      label: 'Triggers',
      format: (r) => formatTriggerList(r.triggers),
    },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    {
      key: 'contentMatchesCategory',
      label: 'Content Matches Content Issue',
      type: 'toggle',
      default: false,
    },
    {
      key: 'checkMethodUsed',
      label: 'Detection Method Used',
      type: 'select',
      options: CHECK_METHOD_OPTIONS,
      default: (rule) => rule?.detectionMethods?.[0] || rule?.checkMethod || CHECK_METHOD_OPTIONS[0]?.value,
    },
  ],
  expectedFields: [
    {
      key: 'expectedScreeningOutcome',
      label: 'Expected Screening Outcome',
      type: 'select',
      required: true,
      options: SCREENING_OUTCOME_OPTIONS,
      default: 'pass',
    },
  ],
  evaluate(rule, form) {
    const ruleMethods = rule?.detectionMethods || (rule?.checkMethod ? [rule.checkMethod] : []);
    const methodMatch = ruleMethods.includes(form.checkMethodUsed);
    const breachDetected = Boolean(form.contentMatchesCategory) && methodMatch;
    let screeningOutcome = 'pass';
    let messageShown = 'Content passed screening.';
    let nextAction = 'Continue';

    if (breachDetected) {
      const action = rule?.confirmedAction || rule?.primaryAction || '';
      if (action === 'block_current_action' || ['block_save', 'block_submit', 'block_send'].includes(action)) {
        screeningOutcome = 'block';
        messageShown = 'Content blocked by screening rule.';
        nextAction = 'Edit content before continuing';
      } else if (action === 'hold_for_review' || action === 'show_warning') {
        screeningOutcome = 'flag';
        messageShown = action === 'hold_for_review' ? 'Content held for review.' : 'Warning shown to user.';
        nextAction = 'Review flagged content';
      }
    } else if (form.contentMatchesCategory && !methodMatch) {
      screeningOutcome = 'pass';
      messageShown = 'Issue signal detected but detection method did not match rule.';
      nextAction = 'No rule action applied';
    }

    return {
      actual: { expectedScreeningOutcome: screeningOutcome },
      actualValues: { expectedScreeningOutcome: screeningOutcome },
      messageShown,
      nextAction,
      displayResults: [
        { label: 'Issue Detected', value: form.contentMatchesCategory ? 'Yes' : 'No' },
        { label: 'Detection Method', value: labelFromOptions(CHECK_METHOD_OPTIONS, form.checkMethodUsed) },
        { label: 'Screening Outcome', value: labelFromOptions(SCREENING_OUTCOME_OPTIONS, screeningOutcome) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedScreeningOutcome', label: 'Screening Outcome', options: SCREENING_OUTCOME_OPTIONS },
    ]);
  },
});

const publishingSimulator = createModuleSimulator({
  moduleKey: 'publishing',
  summaryFields: [
    { key: 'module', label: 'Module', format: (r) => getPublishingModuleLabel(r.module) },
    { key: 'displayArea', label: 'Display Area', format: (r) => getDisplayAreaLabel(r.displayArea) },
    { key: 'sourceResult', label: 'Source Result', format: (r) => getSourceResultLabel(r.sourceResult) },
    { key: 'publishingAction', label: 'Publishing Action', format: (r) => getPublishingActionLabel(r.publishingAction) },
    { key: 'displayState', label: 'Display State', format: (r) => getDisplayStateLabel(r.displayState) },
    { key: 'visibilityAudience', label: 'Visibility Audience', format: (r) => getVisibilityAudienceLabel(r.visibilityAudience) },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    {
      key: 'sourceResult',
      label: 'Source Result',
      type: 'select',
      options: (rule) => getSourceResultsForDisplayArea(rule?.displayArea).length
        ? getSourceResultsForDisplayArea(rule?.displayArea)
        : SOURCE_RESULT_OPTIONS,
      default: (rule) => rule?.sourceResult || SOURCE_RESULT_OPTIONS[0]?.value,
    },
    {
      key: 'targetSupplied',
      label: 'Exact target supplied by source',
      type: 'toggle',
      default: true,
    },
  ],
  expectedFields: [
    {
      key: 'expectedDisplayState',
      label: 'Expected Display State',
      type: 'select',
      required: true,
      options: DISPLAY_STATE_OPTIONS,
      default: (rule) => rule?.displayState || 'visible',
    },
    {
      key: 'expectedAudience',
      label: 'Expected Audience',
      type: 'select',
      required: true,
      options: VISIBILITY_AUDIENCE_OPTIONS,
      default: (rule) => rule?.visibilityAudience || 'public',
    },
  ],
  evaluate(rule, form) {
    const sourceMatch = form.sourceResult === rule?.sourceResult;
    const targetSupplied = Boolean(form.targetSupplied);
    const applies = sourceMatch && targetSupplied;

    const displayState = applies ? rule?.displayState || 'hidden' : 'hidden';
    const audience = applies ? rule?.visibilityAudience || 'not_applicable' : 'not_applicable';
    const action = applies ? rule?.publishingAction : '';

    return {
      actual: { expectedDisplayState: displayState, expectedAudience: audience },
      actualValues: { expectedDisplayState: displayState, expectedAudience: audience },
      messageShown: applies ? 'Publishing rule applied to the supplied target.' : 'No Publishing Action is inferred.',
      nextAction: applies ? `Apply ${getPublishingActionLabel(action) || 'the configured action'}` : 'Required hiding remains effective',
      displayResults: [
        { label: 'Source Result', value: getSourceResultLabel(form.sourceResult) },
        { label: 'Exact target supplied', value: targetSupplied ? 'Yes' : 'No' },
        { label: 'Publishing Action', value: applies ? getPublishingActionLabel(action) : 'None' },
        { label: 'Display State', value: getDisplayStateLabel(displayState) },
        { label: 'Audience', value: getVisibilityAudienceLabel(audience) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedDisplayState', label: 'Display State', options: DISPLAY_STATE_OPTIONS },
      { key: 'expectedAudience', label: 'Audience', options: VISIBILITY_AUDIENCE_OPTIONS },
    ]);
  },
});

const documentsSimulator = createModuleSimulator({
  moduleKey: 'documents',
  summaryFields: [
    { key: 'ruleArea', label: 'Module', format: (r) => getDocumentModuleLabel(r.ruleArea || r.module) },
    { key: 'entryPoint', label: 'Entry Point', format: (r) => labelEntryPoint(r.entryPoint) },
    { key: 'uploadAllowed', label: 'Upload Allowed', format: (r) => (r.uploadAllowed ? 'Yes' : 'No') },
    {
      key: 'allowedFileFormats',
      label: 'Allowed Formats',
      format: (r) =>
        (r.allowedFileFormats || [])
          .map((v) => labelFromOptions(FILE_FORMAT_OPTIONS, v))
          .filter(Boolean)
          .join(', ') || '—',
    },
    { key: 'missingDocumentResult', label: 'Missing Document Result', format: (r) => labelFromOptions(MISSING_DOCUMENT_RESULT_OPTIONS, r.missingDocumentResult) },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    { key: 'uploadAttempted', label: 'Upload Attempted', type: 'toggle', default: true },
    {
      key: 'fileFormat',
      label: 'Uploaded File Format',
      type: 'select',
      options: FILE_FORMAT_OPTIONS,
      default: (rule) => rule?.allowedFileFormats?.[0] || 'pdf',
    },
    { key: 'documentPresent', label: 'Document Present', type: 'toggle', default: true },
  ],
  expectedFields: [
    {
      key: 'expectedUploadResult',
      label: 'Expected Upload Result',
      type: 'select',
      required: true,
      options: DOCUMENT_UPLOAD_RESULT_OPTIONS,
      default: 'allowed',
    },
    {
      key: 'expectedMissingResult',
      label: 'Expected Missing Result',
      type: 'select',
      required: true,
      options: MISSING_DOCUMENT_RESULT_OPTIONS,
      default: (rule) => rule?.missingDocumentResult || 'no_action',
    },
  ],
  evaluate(rule, form) {
    const allowedFormats = rule?.allowedFileFormats || [];
    const formatValid = allowedFormats.includes(form.fileFormat);
    const uploadAllowedByRule = Boolean(rule?.uploadAllowed);

    let uploadResult = 'allowed';
    let missingResult = rule?.missingDocumentResult || 'no_action';
    let messageShown = 'Upload permitted.';
    let nextAction = 'Accept upload';

    if (!uploadAllowedByRule && form.uploadAttempted) {
      uploadResult = 'blocked';
      messageShown = 'Upload is not allowed for this entry point.';
      nextAction = 'Block upload attempt';
    } else if (form.uploadAttempted && !formatValid) {
      uploadResult = 'blocked';
      missingResult = rule?.invalidDocumentResult || 'show_prompt';
      messageShown = 'File format is not allowed.';
      nextAction = 'Reject file and show prompt';
    } else if (!form.documentPresent) {
      uploadResult = uploadAllowedByRule ? 'allowed' : 'blocked';
      missingResult = rule?.missingDocumentResult || 'no_action';
      messageShown = 'Required document is missing.';
      nextAction = labelFromOptions(MISSING_DOCUMENT_RESULT_OPTIONS, missingResult);
    } else if (!form.uploadAttempted) {
      missingResult = rule?.missingDocumentResult || 'no_action';
      messageShown = 'No upload attempted.';
      nextAction = 'Prompt for document if required';
    }

    return {
      actual: { expectedUploadResult: uploadResult, expectedMissingResult: missingResult },
      actualValues: { expectedUploadResult: uploadResult, expectedMissingResult: missingResult },
      messageShown,
      nextAction,
      displayResults: [
        { label: 'Upload Attempted', value: form.uploadAttempted ? 'Yes' : 'No' },
        { label: 'Format Valid', value: formatValid ? 'Yes' : 'No' },
        { label: 'Document Present', value: form.documentPresent ? 'Yes' : 'No' },
        { label: 'Upload Result', value: labelFromOptions(DOCUMENT_UPLOAD_RESULT_OPTIONS, uploadResult) },
        { label: 'Missing Result', value: labelFromOptions(MISSING_DOCUMENT_RESULT_OPTIONS, missingResult) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedUploadResult', label: 'Upload Result', options: DOCUMENT_UPLOAD_RESULT_OPTIONS },
      { key: 'expectedMissingResult', label: 'Missing Result', options: MISSING_DOCUMENT_RESULT_OPTIONS },
    ]);
  },
});

const caseActionsSimulator = createModuleSimulator({
  moduleKey: 'case-actions',
  summaryFields: [
    { key: 'module', label: 'Module', format: (r) => getCaseActionModuleLabel(r.module) },
    { key: 'recordType', label: 'Record Type', format: (r) => getCaseActionRecordTypeLabel(r.module, r.recordType) },
    { key: 'actionType', label: 'Action Type', format: (r) => getActionTypeLabel(r.actionType) },
    { key: 'actionTarget', label: 'Action Target', format: (r) => getActionTargetLabel(r.module, r.recordType, r.actionTarget || r.actionTargetResolution) },
    {
      key: 'requirements',
      label: 'Requirements',
      format: (r) => {
        const n = normalizeCaseActionForm(r || {});
        return [
          formatRequirementPreview(n.reasonRequirement),
          formatRequirementPreview(n.evidenceRequirement),
          formatRequirementPreview(n.confirmationRequirement),
        ].join(' · ');
      },
    },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    { key: 'scopeAndConditionsQualify', label: 'Scope, Conditions and required references qualify', type: 'toggle', default: true },
    { key: 'submissionRequirementsComplete', label: 'Required submission inputs are complete', type: 'toggle', default: true },
    { key: 'canCheckAvailability', label: 'Availability can be checked', type: 'toggle', default: true },
  ],
  expectedFields: [
    {
      key: 'expectedActionAvailability',
      label: 'Expected Action Availability',
      type: 'select',
      required: true,
      options: ACTION_AVAILABILITY_OPTIONS,
      default: () => 'available',
    },
  ],
  evaluate(rule, form) {
    const snapshot = normalizeCaseActionForm(rule || {});
    const canCheck = Boolean(form.canCheckAvailability);
    const qualifies = Boolean(form.scopeAndConditionsQualify);
    const referencesOk = !requiresEvidenceSource(snapshot.evidenceRequirement) || isEvidenceSourceAvailable(snapshot.evidenceSource);
    const submissionOk = Boolean(form.submissionRequirementsComplete);
    const available = canCheck && qualifies && referencesOk && submissionOk;
    const availability = available ? 'available' : 'blocked';

    let messageShown = 'Action is available for the current case and target.';
    let nextAction = `Use ${getActionTypeLabel(snapshot.actionType) || 'the action'} — the action is not performed automatically.`;
    if (!canCheck) {
      messageShown = 'We could not confirm that this action is available. Refresh the case and try again.';
      nextAction = 'Refresh the case and try again';
    } else if (!available) {
      messageShown = 'This action is no longer available. Refresh the case and try again.';
      nextAction = 'Refresh the case and try again';
    }

    return {
      actual: { expectedActionAvailability: availability },
      actualValues: { expectedActionAvailability: availability },
      messageShown,
      nextAction,
      displayResults: [
        { label: 'Scope, Conditions and references', value: qualifies && referencesOk ? 'Qualify' : 'Do not qualify' },
        { label: 'Submission requirements', value: submissionOk ? 'Complete' : 'Incomplete' },
        { label: 'Availability can be checked', value: canCheck ? 'Yes' : 'No' },
        { label: 'Action Type', value: getActionTypeLabel(snapshot.actionType) || labelFromOptions(ACTION_TYPE_OPTIONS, snapshot.actionType) },
        { label: 'Availability', value: labelFromOptions(ACTION_AVAILABILITY_OPTIONS, availability) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedActionAvailability', label: 'Action Availability', options: ACTION_AVAILABILITY_OPTIONS },
    ]);
  },
});

const limitsSimulator = createModuleSimulator({
  moduleKey: 'limits',
  summaryFields: [
    { key: 'requirement', label: 'Requirement', format: (r) => getRequirementLabel(r.requirement) },
    { key: 'limitFor', label: 'Limit For', format: (r) => getLimitForLabel(r.limitFor) },
    { key: 'limitType', label: 'Limit Type', format: (r) => getLimitTypeLabel(r.limitType) },
    { key: 'limit', label: 'Limit', format: (r) => formatLimitDisplay(r) },
    { key: 'unit', label: 'Unit', format: (r) => getUnitLabel(r.unit) },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    { key: 'measurementAvailable', label: 'Required measurement data is available', type: 'toggle', default: true },
    { key: 'conditionsQualify', label: 'Conditions match this case', type: 'toggle', default: true },
    {
      key: 'measuredValue',
      label: 'Measured value',
      type: 'text',
      required: false,
      default: '',
    },
  ],
  expectedFields: [
    {
      key: 'expectedComparison',
      label: 'Expected comparison',
      type: 'select',
      required: true,
      options: LIMIT_COMPARISON_OPTIONS,
      default: 'within',
    },
  ],
  evaluate(rule, form) {
    const snapshot = normalizeLimitForm(rule || {});
    const measurementAvailable = Boolean(form.measurementAvailable);
    const conditionsQualify = !(snapshot.conditions || []).length || Boolean(form.conditionsQualify);
    const parsed = parseEnteredNumber(form.measuredValue);
    const hasFiniteMeasurement = measurementAvailable && parsed.finite;
    let comparison = 'unavailable';
    let messageShown = 'Required measurement data is unavailable. It is not treated as zero.';
    let nextAction = 'Do not apply this Rule. Missing required data does not become a numeric result.';

    if (!measurementAvailable || !hasFiniteMeasurement) {
      comparison = 'unavailable';
      messageShown = 'Required measurement data is unavailable. It is not treated as zero.';
      nextAction = 'Do not apply this Rule. Missing required data does not become a numeric result.';
    } else if (!conditionsQualify) {
      comparison = 'unavailable';
      messageShown = 'The Rule does not apply because its Conditions do not match this case.';
      nextAction = 'Leave the measurement unused by this Rule.';
    } else {
      const within = valueSatisfiesRule(parsed.number, snapshot);
      comparison = within ? 'within' : 'outside';
      messageShown = within
        ? 'The measured value is within the configured restriction.'
        : 'The measured value is outside the configured restriction.';
      nextAction = within
        ? 'Use the Rule as configured. Do not round the measured value before comparison.'
        : 'The configured restriction does not contain this measured value.';
    }

    return {
      actual: { expectedComparison: comparison },
      actualValues: { expectedComparison: comparison },
      messageShown,
      nextAction,
      displayResults: [
        { label: 'Measurement available', value: measurementAvailable && hasFiniteMeasurement ? 'Yes' : 'No' },
        { label: 'Conditions match', value: conditionsQualify ? 'Yes' : 'No' },
        { label: 'Measured value', value: hasFiniteMeasurement ? parsed.text : 'Unavailable' },
        { label: 'Limit', value: formatLimitDisplay(snapshot) },
        { label: 'Unit', value: getUnitLabel(snapshot.unit) || '—' },
        { label: 'Comparison', value: labelFromOptions(LIMIT_COMPARISON_OPTIONS, comparison) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedComparison', label: 'Comparison', options: LIMIT_COMPARISON_OPTIONS },
    ]);
  },
});

const automationSimulator = createModuleSimulator({
  moduleKey: 'automation',
  summaryFields: [
    { key: 'module', label: 'Module', format: (r) => getAutomationModuleLabel(r.module) },
    { key: 'recordType', label: 'Record Type', format: (r) => getRecordTypeLabel(r.recordType) },
    { key: 'automatedJob', label: 'Automated Job', format: (r) => getAutomatedJobLabel(r.automatedJob) },
    { key: 'schedule', label: 'Schedule', format: (r) => r.schedule || 'Not Applicable' },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    { key: 'scheduleDue', label: 'Schedule is due', type: 'toggle', default: true },
    { key: 'referencesAvailable', label: 'Required references available', type: 'toggle', default: true },
    { key: 'conditionsMatch', label: 'Conditions match', type: 'toggle', default: true },
  ],
  expectedFields: [
    {
      key: 'expectedRun',
      label: 'Expected Run',
      type: 'select',
      required: true,
      options: [
        { value: 'run_job', label: 'Start the Automated Job' },
        { value: 'no_run', label: 'Do not start a run' },
      ],
      default: 'run_job',
    },
  ],
  evaluate(rule, form) {
    const due = Boolean(form.scheduleDue);
    const available = Boolean(form.referencesAvailable);
    const conditionsMatch = !(rule?.conditions || []).length || Boolean(form.conditionsMatch);
    const applies = due && available && conditionsMatch;
    const expectedRun = applies ? 'run_job' : 'no_run';

    return {
      actual: { expectedRun },
      actualValues: { expectedRun },
      messageShown: applies ? 'Automation Rule qualified for a new run.' : 'No new run is started.',
      nextAction: applies
        ? `Run ${getAutomatedJobLabel(rule?.automatedJob) || 'the Automated Job'}`
        : 'Keep the Rule stored without starting a run',
      displayResults: [
        { label: 'Schedule is due', value: due ? 'Yes' : 'No' },
        { label: 'Required references available', value: available ? 'Yes' : 'No' },
        { label: 'Conditions match', value: conditionsMatch ? 'Yes' : 'No' },
        { label: 'Automated Job', value: getAutomatedJobLabel(rule?.automatedJob) || '—' },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      {
        key: 'expectedRun',
        label: 'Run',
        options: [
          { value: 'run_job', label: 'Start the Automated Job' },
          { value: 'no_run', label: 'Do not start a run' },
        ],
      },
    ]);
  },
});

const lifecycleSimulator = createModuleSimulator({
  moduleKey: 'lifecycle',
  summaryFields: [
    { key: 'module', label: 'Module', format: (r) => labelFromOptions(LIFECYCLE_MODULES, r.module) },
    { key: 'recordType', label: 'Record Type', format: (r) => r.recordType || '—' },
    {
      key: 'endEvent',
      label: 'End Event',
      format: (r) => getEndEventLabel(r.endEvent || r.lifecycleTrigger),
    },
    {
      key: 'archiveAction',
      label: 'Archive Action',
      format: (r) => getArchiveActionLabel(r.archiveAction || r.archiveRule),
    },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    {
      key: 'triggerEvent',
      label: 'End Event',
      type: 'select',
      options: () => END_EVENT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      default: (rule) => {
        const n = normalizeLifecycleForm(rule || {});
        return n.endEvent || END_EVENT_OPTIONS[0]?.value;
      },
    },
    {
      key: 'completionSatisfied',
      label: 'Completion requirements satisfied',
      type: 'toggle',
      default: true,
    },
  ],
  expectedFields: [
    {
      key: 'expectedLifecycleOutcome',
      label: 'Expected Lifecycle Outcome',
      type: 'select',
      required: true,
      options: LIFECYCLE_OUTCOME_OPTIONS,
      default: 'archived',
    },
  ],
  evaluate(rule, form) {
    const snapshot = normalizeLifecycleForm(rule || {});
    const eventMatch = form.triggerEvent === snapshot.endEvent;
    const complete = Boolean(form.completionSatisfied);
    let lifecycleOutcome = 'no_change';
    let messageShown = 'No Archive movement applied.';
    let nextAction = 'Keep the case in Queue';

    if (eventMatch && complete && snapshot.archiveAction) {
      lifecycleOutcome = 'archived';
      messageShown = 'Case qualifies for Archive movement.';
      nextAction = getArchiveActionLabel(snapshot.archiveAction) || 'Move to Archive';
    } else if (eventMatch && !complete) {
      messageShown = 'End Event occurred but completion requirements are unfinished.';
      nextAction = 'Complete required outcome work before Archive movement';
    }

    return {
      actual: { expectedLifecycleOutcome: lifecycleOutcome },
      actualValues: { expectedLifecycleOutcome: lifecycleOutcome },
      messageShown,
      nextAction,
      displayResults: [
        { label: 'End Event', value: getEndEventLabel(form.triggerEvent) || labelFromOptions(LIFECYCLE_TRIGGER_OPTIONS, form.triggerEvent) },
        { label: 'Completion satisfied', value: complete ? 'Yes' : 'No' },
        { label: 'Archive Action', value: getArchiveActionLabel(snapshot.archiveAction) || '—' },
        { label: 'Lifecycle Outcome', value: labelFromOptions(LIFECYCLE_OUTCOME_OPTIONS, lifecycleOutcome) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedLifecycleOutcome', label: 'Lifecycle Outcome', options: LIFECYCLE_OUTCOME_OPTIONS },
    ]);
  },
});

const retentionSimulator = createModuleSimulator({
  moduleKey: 'retention',
  summaryFields: [
    { key: 'recordType', label: 'Record Type', format: (r) => getRetentionRecordTypeLabel(r.recordType) },
    { key: 'startsWhen', label: 'Starts When', format: (r) => getStartsWhenLabel(r.startsWhen || r.retentionTrigger) },
    { key: 'retentionPeriod', label: 'Retention Period', format: (r) => labelRetentionPeriod(r) },
    { key: 'finalAction', label: 'Final Action', format: (r) => getFinalActionLabel(r.finalAction, r.recordType) },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    { key: 'startOccurrenceValid', label: 'Start occurrence is valid', type: 'toggle', default: true },
    { key: 'conditionsPassAtStart', label: 'Conditions pass at start', type: 'toggle', default: true },
    { key: 'periodElapsed', label: 'Original Retention Period has ended', type: 'toggle', default: false },
    { key: 'conditionsPassBeforeFinal', label: 'Conditions pass before Final Action', type: 'toggle', default: true },
    { key: 'finalActionSupported', label: 'Final Action remains supported', type: 'toggle', default: true },
    { key: 'checksCanBeCompleted', label: 'Required checks can be completed', type: 'toggle', default: true },
  ],
  expectedFields: [
    {
      key: 'expectedRetentionOutcome',
      label: 'Expected Retention Outcome',
      type: 'select',
      required: true,
      options: RETENTION_OUTCOME_OPTIONS,
      default: 'retain',
    },
  ],
  evaluate(rule, form) {
    const snapshot = normalizeRetentionForm(rule || {});
    const canCheck = Boolean(form.checksCanBeCompleted);
    const startValid = Boolean(form.startOccurrenceValid);
    const startConditionsPass = !(snapshot.conditions || []).length || Boolean(form.conditionsPassAtStart);
    const started = canCheck && startValid && startConditionsPass;
    const periodElapsed = Boolean(form.periodElapsed);
    const finalChecksPass = canCheck
      && Boolean(form.conditionsPassBeforeFinal || !(snapshot.conditions || []).length)
      && Boolean(form.finalActionSupported);
    const retentionOutcome = !started
      ? 'retain'
      : mapFinalActionToOutcome(snapshot.finalAction, periodElapsed, finalChecksPass);

    let messageShown = 'Record remains within retention period.';
    let nextAction = 'Keep the record retained with the original start and deadline.';
    if (!canCheck) {
      messageShown = 'A required check cannot be completed. Retention does not start or the Final Action is not performed.';
      nextAction = 'Keep the original Rule settings. Do not substitute another Rule or Final Action.';
    } else if (!started) {
      messageShown = 'Retention does not start under this Rule.';
      nextAction = 'Do not start retention. A missing or unavailable required value does not count as a match.';
    } else if (periodElapsed && !finalChecksPass) {
      messageShown = 'The Final Action is not performed. The record remains retained with the original start and deadline.';
      nextAction = 'Keep the original Rule settings. A later successful check may permit the same Final Action after the original deadline.';
    } else if (periodElapsed && finalChecksPass) {
      messageShown = 'The original Retention Period has ended and the required final checks passed.';
      nextAction = getFinalActionLabel(snapshot.finalAction, snapshot.recordType) || 'Perform the Final Action';
    }

    return {
      actual: { expectedRetentionOutcome: retentionOutcome },
      actualValues: { expectedRetentionOutcome: retentionOutcome },
      messageShown,
      nextAction,
      displayResults: [
        { label: 'Start occurrence is valid', value: startValid ? 'Yes' : 'No' },
        { label: 'Conditions at start', value: startConditionsPass ? 'Pass' : 'Fail' },
        { label: 'Period ended', value: periodElapsed ? 'Yes' : 'No' },
        { label: 'Final checks', value: finalChecksPass ? 'Pass' : 'Fail' },
        { label: 'Retention Period', value: getRetentionPeriodLabel(snapshot.retentionPeriod) || labelRetentionPeriod(snapshot) },
        { label: 'Retention Outcome', value: labelFromOptions(RETENTION_OUTCOME_OPTIONS, retentionOutcome) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedRetentionOutcome', label: 'Retention Outcome', options: RETENTION_OUTCOME_OPTIONS },
    ]);
  },
});

const reportingSimulator = createModuleSimulator({
  moduleKey: 'reporting',
  summaryFields: [
    { key: 'flagType', label: 'Flag Type', format: (r) => getFlagTypeLabel(r.flagType) },
    { key: 'flagReasonSet', label: 'Flag Reason Set', format: (r) => r.flagReasonSet || '—' },
    { key: 'submittedBy', label: 'Submitted By', format: (r) => (r.submittedBy || []).join(', ') || '—' },
    { key: 'duplicateCheck', label: 'Duplicate Check', format: (r) => r.duplicateCheck || '—' },
    { key: 'submissionResult', label: 'Submission Result', format: (r) => labelFromOptions(SUBMISSION_RESULT_OPTIONS, r.submissionResult) },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    {
      key: 'flagReason',
      label: 'Flag Reason',
      type: 'select',
      options: (rule) => getReasonItemsForSet(rule?.flagReasonSet),
      default: (rule, opts) => opts[0]?.value || '',
    },
    {
      key: 'reporterType',
      label: 'Reporter Type',
      type: 'select',
      options: FLAGGING_SUBMITTED_BY_OPTIONS,
      default: 'customer',
    },
  ],
  expectedFields: [
    {
      key: 'expectedFlagOutcome',
      label: 'Expected Flag Outcome',
      type: 'select',
      required: true,
      options: FLAG_OUTCOME_OPTIONS,
      default: 'created',
    },
  ],
  evaluate(rule, form) {
    const allowedReporters = rule?.submittedBy || [];
    const reasonOptions = getReasonItemsForSet(rule?.flagReasonSet);
    const reasonAllowed = reasonOptions.some((o) => o.value === form.flagReason);
    const reporterAllowed = allowedReporters.includes(form.reporterType);
    const flagOutcome = reasonAllowed && reporterAllowed ? 'created' : 'suppressed';

    return {
      actual: { expectedFlagOutcome: flagOutcome },
      actualValues: { expectedFlagOutcome: flagOutcome },
      messageShown:
        flagOutcome === 'created' ? 'Flag submitted successfully.' : 'Flag submission was suppressed.',
      nextAction: flagOutcome === 'created' ? 'Create moderation case' : 'Review reporter or reason eligibility',
      displayResults: [
        { label: 'Flag Reason', value: labelFromOptions(reasonOptions, form.flagReason) },
        { label: 'Reporter Type', value: labelFromOptions(FLAGGING_SUBMITTED_BY_OPTIONS, form.reporterType) },
        { label: 'Flag Outcome', value: labelFromOptions(FLAG_OUTCOME_OPTIONS, flagOutcome) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedFlagOutcome', label: 'Flag Outcome', options: FLAG_OUTCOME_OPTIONS },
    ]);
  },
});

const notificationsSimulator = createModuleSimulator({
  moduleKey: 'notifications',
  summaryFields: [
    { key: 'event', label: 'Event', format: (r) => getEventDisplay(r.event) },
    { key: 'recipient', label: 'Recipient Type', format: (r) => getRecipientLabel(r.recipient) },
    { key: 'sendTo', label: 'Recipient', format: (r) => getSendToLabel(r.sendTo) },
    { key: 'method', label: 'Method', format: (r) => getMethodLabel(r.method) },
    { key: 'type', label: 'Required / Optional', format: (r) => getTypeLabel(r.type) },
    { key: 'timing', label: 'Timing', format: (r) => getTimingLabel(r.timing) },
  ],
  setupFields: [SCENARIO_NAME_FIELD],
  inputFields: [
    { key: 'eventOccurred', label: 'Event has occurred', type: 'toggle', default: true },
    { key: 'recipientResolved', label: 'Recipient relationship resolved', type: 'toggle', default: true },
    { key: 'conditionsMatch', label: 'Conditions match this case', type: 'toggle', default: true },
    {
      key: 'preferenceAllows',
      label: 'Recipient preference allows this notification',
      type: 'toggle',
      default: true,
    },
    {
      key: 'timingReferencesAvailable',
      label: 'Required timing references are available',
      type: 'toggle',
      default: (rule) => {
        const snapshot = normalizeNotificationForm(rule || {});
        if (snapshot.timing !== 'threshold_based') return true;
        const required = ['initialDelay', 'maximumSends'];
        if (needsRepeatInterval(snapshot.maximumSends)) required.push('repeatInterval');
        return required.every((field) => {
          const detail = formatTimingReferenceDetail(snapshot[field]);
          return snapshot[field] && !detail.paused && detail.availability !== 'Unavailable';
        });
      },
    },
  ],
  expectedFields: [
    {
      key: 'expectedDeliveryOutcome',
      label: 'Expected Delivery Outcome',
      type: 'select',
      required: true,
      options: NOTIFICATION_OUTCOME_OPTIONS,
      default: 'sent',
    },
  ],
  evaluate(rule, form) {
    const snapshot = normalizeNotificationForm(rule || {});
    const event = getEventDefinition(snapshot.event);
    const eventOccurred = Boolean(form.eventOccurred);
    const recipientResolved = Boolean(form.recipientResolved);
    const conditionsMatch = !(snapshot.conditions || []).length || Boolean(form.conditionsMatch);
    const preferenceAllows = snapshot.type !== 'optional' || Boolean(form.preferenceAllows);
    const timingNeeded = snapshot.timing === 'threshold_based';
    const timingAvailable = !timingNeeded || Boolean(form.timingReferencesAvailable);
    const eventReady = Boolean(event && event.available !== false && event.complete !== false);

    let deliveryOutcome = 'sent';
    let messageShown = 'Notification sent.';
    let nextAction = `Deliver via ${getMethodLabel(snapshot.method) || 'the selected Method'}`;

    if (!eventReady || (timingNeeded && !timingAvailable)) {
      deliveryOutcome = 'unavailable';
      messageShown = !eventReady
        ? 'This Event definition is unavailable or incomplete.'
        : 'A required timing reference is unavailable.';
      nextAction = 'Do not send. Keep the Rule stored until required references are valid.';
    } else if (!eventOccurred || !recipientResolved || !conditionsMatch) {
      deliveryOutcome = 'do_not_send';
      messageShown = 'The Rule does not apply to this case.';
      nextAction = 'Do not send. The Event, recipient or Conditions do not match.';
    } else if (!preferenceAllows) {
      deliveryOutcome = 'do_not_send';
      messageShown = 'The Optional notification follows the recipient preference.';
      nextAction = 'Do not send. Required notifications are not disabled by preference.';
    } else {
      deliveryOutcome = 'sent';
      messageShown = snapshot.type === 'required'
        ? 'Required notification sent.'
        : 'Optional notification sent.';
      nextAction = `Deliver via ${getMethodLabel(snapshot.method) || 'the selected Method'}`;
    }

    return {
      actual: { expectedDeliveryOutcome: deliveryOutcome },
      actualValues: { expectedDeliveryOutcome: deliveryOutcome },
      messageShown,
      nextAction,
      displayResults: [
        { label: 'Event', value: getEventDisplay(snapshot.event) },
        { label: 'Recipient Type', value: getRecipientLabel(snapshot.recipient) || '—' },
        { label: 'Recipient', value: getSendToLabel(snapshot.sendTo) || '—' },
        { label: 'Method', value: getMethodLabel(snapshot.method) || '—' },
        { label: 'Required / Optional', value: getTypeLabel(snapshot.type) || '—' },
        { label: 'Timing', value: getTimingLabel(snapshot.timing) || '—' },
        { label: 'Event has occurred', value: eventOccurred ? 'Yes' : 'No' },
        { label: 'Recipient resolved', value: recipientResolved ? 'Yes' : 'No' },
        { label: 'Conditions match', value: conditionsMatch ? 'Yes' : 'No' },
        { label: 'Preference allows', value: preferenceAllows ? 'Yes' : 'Not applicable or allowed' },
        { label: 'Timing references', value: timingNeeded ? (timingAvailable ? 'Available' : 'Unavailable') : 'Not Applicable' },
        { label: 'Delivery Outcome', value: labelFromOptions(NOTIFICATION_OUTCOME_OPTIONS, deliveryOutcome) },
      ],
    };
  },
  compareChecks(form, evaluation) {
    return compareExpected(form, evaluation, [
      { key: 'expectedDeliveryOutcome', label: 'Delivery Outcome', options: NOTIFICATION_OUTCOME_OPTIONS },
    ]);
  },
});

// ——— exports ———

export const MODULE_SIMULATORS = {
  validation: validationSimulator,
  compliance: complianceSimulator,
  workflow: workflowSimulator,
  'content-control': contentControlSimulator,
  publishing: publishingSimulator,
  documents: documentsSimulator,
  'case-actions': caseActionsSimulator,
  limits: limitsSimulator,
  automation: automationSimulator,
  lifecycle: lifecycleSimulator,
  retention: retentionSimulator,
  reporting: reportingSimulator,
  notifications: notificationsSimulator,
};

export function getModuleSimulator(key) {
  return MODULE_SIMULATORS[key] || null;
}
